import { Fragment, useEffect, useState } from 'react'

import { Separator } from '@/components/ui/separator.tsx'
import { cn } from '@/lib/utils.ts'

import { EstimateIcon } from '@/components/EstimateIcon.tsx'
import { IterationSelector } from '@/components/IterationSelector.tsx'
import { StoryTypeIcon } from '@/components/StoryTypeIcon.tsx'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useQueryAnalyticsIterations } from '@/requests/queries/useQueryAnalytics.tsx'
import { toReadableDurationTime } from '@/utils/date.ts'
import { isAccepted, isInProgress } from '@/utils/status.ts'
import { isPointAssignable } from '@/utils/story.ts'
import { getInitials } from '@/utils/user.ts'

export const StoryCycleTimeDetailsPage = () => {
  const { apiKey } = useUserContext()
  const { iterations } = useQueryAnalyticsIterations(apiKey)
  const currentIteration = iterations?.[iterations.length - 1]

  const [selectedIteration, setSelectedIteration] = useState<IterationSummary | undefined>(
    iterations?.[iterations.length - 1],
  )

  useEffect(() => {
    setSelectedIteration(currentIteration)
  }, [currentIteration])

  if (!selectedIteration) return

  return (
    <div className="w-full">
      <div className="flex gap-1 items-center">
        <p className="font-EN-KR-sub-heading-bold text-contents-primary !font-semibold">Story Cycle Time Report</p>
      </div>
      <Separator className="mt-2.5 mb-4 bg-border-default-secondary" />

      <table className="w-full rounded-md overflow-hidden">
        <thead className="font-EN-KR-caption-regular bg-surface-primary">
          <tr className="text-white">
            <th
              className="px-4 py-2 font-EN-KR-body2-bold border-none shadow-[inset_-1px_0_0_0_theme(colors.border.default.tertiary)]"
              rowSpan={2}
            >
              <div className="flex flex-row min-w-[224px] gap-2 items-center">
                Iteration
                <IterationSelector iterations={iterations} setSelectedIteration={setSelectedIteration} />
              </div>
            </th>
            <th
              className="border-none w-[72px] font-EN-KR-caption-regular text-center align-bottom py-1 px-2 text-contents-accent border-[1px]"
              rowSpan={2}
            >
              Cycle time
            </th>
            <th
              className="border-none w-[72px] shadow-[inset_-1px_0_0_0_theme(colors.border.default.tertiary)] font-EN-KR-caption-regular text-center align-bottom py-1 px-2 text-contents-accent border-[1px]"
              rowSpan={2}
            >
              Rejections
            </th>
            <th
              className="border-none shadow-[inset_0_-1px_0_0_theme(colors.border.default.tertiary)] font-EN-KR-caption-bold text-center align-middle py-1 px-2 text-contents-secondary border-[1px]"
              colSpan={4}
            >
              Time spent in state
            </th>
          </tr>

          <tr className="bg-[#34495e] text-[#10a8ff]">
            <th className="w-[72px] py-1 font-EN-KR-caption-regular">Started</th>
            <th className="w-[72px] py-1 font-EN-KR-caption-regular">Finished</th>
            <th className="w-[72px] py-1 font-EN-KR-caption-regular">Delivered</th>
            <th className="w-[72px] py-1 font-EN-KR-caption-regular">Rejected</th>
          </tr>
        </thead>
        <tbody className="font-EN-KR-caption-regular text-center align-middle divide-y divide-surface-background">
          {selectedIteration.stories
            .filter((story) => story.storyType !== STORY_TYPES.RELEASE && story.cycleTimeDetails.totalCycleTime > 0)
            .map((story) => (
              <tr key={story.id} className={cn('divide-x divide-surface-background bg-surface-componentBackground')}>
                <StoryElement story={story} />
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}

const StoryElement = ({ story }: { story: StoryWithAnalyticsDetails }) => {
  const ownersList = (owners: Member[]) => {
    if (owners.length === 0) return null

    const result = owners.map((owner, index) => (
      <Fragment key={owner.id}>
        {index > 0 && <span className="text-tb-navy-900">, </span>}
        <span className="text-contents-accent">{getInitials(owner.name)}</span>
      </Fragment>
    ))

    return <span>({result})</span>
  }

  return (
    <>
      <td
        className={cn(
          'font-EN-KR-caption-regular text-center align-middle pl-3 pr-2 py-1 px-2 flex flex-row gap-2',
          sideBorderStyle(story.status),
        )}
      >
        <div className="flex flex-col items-center gap-1">
          <div className="h-5 py-0 flex items-center justify-center px-1 bg-tb-grey-000 rounded-xs hover:bg-tb-grey-000">
            <StoryTypeIcon className="w-3 h-3" storyType={story.storyType} />
          </div>
        </div>
        <div className="h-5 w-[14px]">
          {isPointAssignable(story.storyType) && (
            <EstimateIcon className="h-5 w-[14px] rounded-sm" estimate={story.estimate} />
          )}
        </div>

        <div className="flex flex-col items-start gap-1 flex-1 break-all font-EN-KR-body1-regular">
          <div className="text-[14px] leading-5">
            <div className="flex gap-1 items-center">
              <span className="text-contents-primary text-left">
                {story.title} {ownersList(story.owners)}
              </span>
            </div>
          </div>
          <span>
            [
            <span className="text-contents-accent">
              #<span>{story.id}</span>
            </span>
            ]
          </span>
          <div className="flow-root text-left">
            {story.labels.map((label, index) => (
              <Fragment key={label.id}>
                <span className="text-labelType-label px-0 font-EN-KR-tag-bold">{label.name}</span>
                {index < story.labels.length - 1 && (
                  <span className="text-contents-primary font-EN-KR-tag-bold">,&nbsp;</span>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </td>
      <td className="font-EN-KR-caption-regular text-center align-middle py-1 px-2">
        {toReadableDurationTime(story.cycleTimeDetails.totalCycleTime)}
      </td>
      <td className="font-EN-KR-caption-regular text-center align-middle py-1 px-2">{`${story.cycleTimeDetails.rejectedCount} rejection`}</td>
      <td className="font-EN-KR-caption-regular text-center align-middle py-1 px-2">
        {toReadableDurationTime(story.cycleTimeDetails.startedTime)}
      </td>
      <td className="font-EN-KR-caption-regular text-center align-middle py-1 px-2">
        {toReadableDurationTime(story.cycleTimeDetails.finishedTime)}
      </td>
      <td className="font-EN-KR-caption-regular text-center align-middle py-1 px-2">
        {toReadableDurationTime(story.cycleTimeDetails.deliveredTime)}
      </td>
      <td className="font-EN-KR-caption-regular text-center align-middle py-1 px-2">
        {toReadableDurationTime(story.cycleTimeDetails.rejectedTime)}
      </td>
    </>
  )
}

const sideBorderStyle = (status: StoryStatus) => {
  switch (true) {
    case isAccepted(status):
      return 'shadow-[inset_4px_0_0_0_theme(colors.surface.storyStatusBar.done)]'
    case isInProgress(status):
      return 'shadow-[inset_4px_0_0_0_theme(colors.surface.storyStatusBar.WIP)]'
    default:
      return 'shadow-[inset_4px_0_0_0_theme(colors.surface.storyStatusBar.toDo)]'
  }
}
